/* global React, Icon, Button, IconButton, Field, Input, Modal, toast, t */
// PLUMME 3 - Kommentarfelt med lagrede standardkommentarer.
//
// Den samme kommentaren skrives om igjen hver gang man foelger opp ubetalte
// fakturaer. Feltet her lar teksten lagres med et navn og hentes fram igjen,
// og bruker samme lager som kommentarfeltet i salgseditoren
// (`settings.savedInvoiceComments`) — en kommentar lagret ett sted skal finnes
// igjen det andre.
//
// Navnet er det man kjenner igjen kommentaren paa i lista, og kreves derfor naar
// den lagres. Kommentarer lagret for navnefeltet fantes vises med teksten sin.

function invoiceReminderSavedComments() {
  const list = window.PlummeBackend?.getSavedInvoiceComments?.();
  return Array.isArray(list) ? list : [];
}

async function invoiceReminderPersistComments(list) {
  if (!window.PlummeBackend?.saveSavedInvoiceComments) throw new Error(t('inv.comment.notConnected'));
  return window.PlummeBackend.saveSavedInvoiceComments(list);
}

function invoiceReminderCommentLabel(comment) {
  const name = String(comment?.name || '').trim();
  if (name) return name;
  const text = String(comment?.text || '').trim();
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

// Lista ligger i foretakets state, som ogsaa endres andre steder i appen.
// Hendelsene er de samme som salgseditoren lytter paa.
function useSavedInvoiceComments() {
  const [list, setList] = React.useState(invoiceReminderSavedComments);
  React.useEffect(() => {
    const refresh = () => setList(invoiceReminderSavedComments());
    window.addEventListener('plumme-saved-comments-changed', refresh);
    window.addEventListener('plumme-backend-state', refresh);
    refresh();
    return () => {
      window.removeEventListener('plumme-saved-comments-changed', refresh);
      window.removeEventListener('plumme-backend-state', refresh);
    };
  }, []);
  return [list, setList];
}

function InvoiceSavedCommentPicker({ list, onUse, onChange, onClose }) {
  const [busy, setBusy] = React.useState(false);
  const remove = async (id) => {
    try {
      setBusy(true);
      const next = await invoiceReminderPersistComments(list.filter((comment) => comment.id !== id));
      onChange(next);
      toast(t('inv.comment.deleted'), 'info');
    } catch (error) {
      toast(error?.message || t('inv.comment.deleteFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <Modal open portal center onClose={onClose} title={t('inv.comment.savedTitle')} max={560}
    footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}>
    <div className="col gap-10" style={{ paddingBottom: 4 }}>
      {list.length === 0
        ? <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('inv.comment.empty')}</div>
        : list.map((comment) => <div key={comment.id} className="inv-saved-comment">
          <div className="col gap-4" style={{ minWidth: 0 }}>
            <b className="t-sm">{invoiceReminderCommentLabel(comment)}</b>
            <span className="t-xs muted" style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</span>
          </div>
          <div className="row gap-6" style={{ flex: 'none' }}>
            <Button size="sm" variant="secondary" icon="check" disabled={busy} onClick={() => { onUse(comment); onClose(); }}>{t('inv.comment.use')}</Button>
            <IconButton name="trash" size="sm" variant="ghost" disabled={busy} title={t('list.delete')} onClick={() => remove(comment.id)} />
          </div>
        </div>)}
    </div>
  </Modal>;
}

function InvoiceSavedCommentSaveDialog({ text, comment, list, onSaved, onChange, onClose }) {
  const [name, setName] = React.useState(() => String(comment?.name || '').trim());
  const [busy, setBusy] = React.useState(false);
  const updating = Boolean(comment);
  const save = async (asNew) => {
    const trimmedName = name.trim();
    if (!trimmedName) { toast(t('inv.comment.needName'), 'info'); return; }
    const now = new Date().toISOString();
    const shouldUpdate = updating && !asNew;
    const next = shouldUpdate
      ? list.map((entry) => entry.id === comment.id ? { ...entry, name: trimmedName, text, updatedAt: now } : entry)
      : [{ id: `sc${Date.now()}`, name: trimmedName, text, createdAt: now, updatedAt: now }, ...list];
    try {
      setBusy(true);
      const saved = await invoiceReminderPersistComments(next);
      onChange(saved);
      // Etter lagring peker feltet paa kommentaren som ble lagret, slik at neste
      // trykk oppdaterer den samme i stedet for aa lage en kopi til.
      const stored = saved.find((entry) => shouldUpdate ? entry.id === comment.id : entry.text === text && entry.name === trimmedName);
      onSaved(stored || null);
      toast(t(shouldUpdate ? 'inv.comment.updated' : 'inv.comment.saved'), 'success');
      onClose();
    } catch (error) {
      toast(error?.message || t('inv.comment.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <Modal open portal center onClose={onClose} title={t(updating ? 'inv.comment.updateTitle' : 'inv.comment.saveTitle')} max={520}
    footer={<React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
      {updating && <Button variant="secondary" icon="files" disabled={busy} onClick={() => save(true)}>{t('inv.comment.saveAsNew')}</Button>}
      <Button icon="save" disabled={busy} onClick={() => save(false)}>{t(updating ? 'inv.comment.update' : 'inv.comment.save')}</Button>
    </React.Fragment>}>
    <div className="col gap-12" style={{ paddingBottom: 4 }}>
      <Field label={t('inv.comment.name')} hint={t('inv.comment.nameHint')}>
        <Input value={name} maxLength={60} placeholder={t('inv.comment.namePh')} onChange={(event) => setName(event.target.value)} />
      </Field>
      <div className="inv-saved-comment-preview t-sm">{text}</div>
    </div>
  </Modal>;
}

// Selve feltet: fritekst med «Bruk kommentar» og «Lagre/Oppdater kommentar» under.
// `savedId` holder hvilken lagret kommentar teksten kom fra, slik at knappen kan
// oppdatere den i stedet for aa legge igjen en ny nesten lik kommentar.
function InvoiceCommentField({ label, hint, placeholder, value, onChange, savedId, onSavedIdChange, rows = 3, maxLength = 2000 }) {
  const [list, setList] = useSavedInvoiceComments();
  const [picking, setPicking] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const text = String(value || '').trim();
  const active = list.find((comment) => comment.id === savedId) || null;
  // Er teksten endret etter at den ble hentet fra lista, er det fortsatt den
  // samme kommentaren man holder paa med — da skal knappen oppdatere den.
  const updating = Boolean(active);
  const setSavedId = (id) => { if (onSavedIdChange) onSavedIdChange(id || ''); };
  return <React.Fragment>
    <Field label={label || t('inv.comment.label')} hint={hint || t('inv.comment.hint')}>
      <textarea className="input" rows={rows} maxLength={maxLength} placeholder={placeholder || t('inv.comment.placeholder')}
        value={value || ''} onChange={(event) => onChange(event.target.value)} />
    </Field>
    <div className="row gap-16 inv-comment-actions">
      <button type="button" onClick={() => setPicking(true)}><span className="row gap-6"><Icon name="list" size={14} />{t('inv.comment.useSaved')}</span></button>
      <button type="button" disabled={!text} onClick={() => setSaving(true)}><span className="row gap-6"><Icon name="save" size={14} />{t(updating ? 'inv.comment.update' : 'inv.comment.save')}</span></button>
    </div>
    {picking && <InvoiceSavedCommentPicker list={list} onChange={setList} onClose={() => setPicking(false)}
      onUse={(comment) => { onChange(comment.text); setSavedId(comment.id); }} />}
    {saving && <InvoiceSavedCommentSaveDialog text={text} comment={active} list={list} onChange={setList}
      onSaved={(comment) => setSavedId(comment?.id || '')} onClose={() => setSaving(false)} />}
  </React.Fragment>;
}

Object.assign(window, { InvoiceCommentField, invoiceReminderCommentLabel });
